import type { User } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { useEffect, useRef } from 'react'

import { analytics, clearAnalyticsDefaults, setAnalyticsDefaults } from '../analytics'

/** Configure telemetry frameworks with user and org info. */
const useTelemetryTags = (user: User | undefined) => {
  const hadUserRef = useRef(false)

  // Update telemetry identities/tags when user is present.
  useEffect(() => {
    if (!user) return
    hadUserRef.current = true

    Sentry.setUser({ id: user.sub, email: user.email })
    Sentry.setTag('org_id', user.org_id ? String(user.org_id) : null)

    const emailHost =
      typeof user.email === 'string' && user.email.includes('@')
        ? user.email.substring(user.email.lastIndexOf('@') + 1).toLowerCase()
        : undefined

    const orgId = user.org_id ? String(user.org_id) : undefined

    // Segment best practices:
    // - identify(): user traits
    // - group(): org/account traits (so org rollups are easy in the warehouse)
    // - source middleware (see `core/analytics.tsx`): common properties on track/page
    // Always update defaults to avoid leaking previous org context if org_id becomes falsy.
    setAnalyticsDefaults({
      orgId,
      emailHost,
      deploymentMode: process.env.MONOMER_FRONTEND_DEPLOYMENT_MODE,
      environment: process.env.NODE_ENV,
      release: process.env.AWS_COMMIT_ID,
    })

    if (orgId) {
      analytics.group(orgId, {
        name: user.org_display_name,
      })
    }

    analytics.identify(user.sub, {
      name: user.name,
      email: user.email,
      avatar: user.picture,
      orgId,
      ...(orgId ? { company: { id: orgId, name: user.org_display_name } } : {}),
    })
  }, [user])

  // Only reset Segment on logout transition (user becomes undefined).
  useEffect(() => {
    if (user) return
    if (!hadUserRef.current) return

    Sentry.setUser(null)
    Sentry.setTag('org_id', null)
    clearAnalyticsDefaults()
    analytics.reset()
  }, [user])

  // Reset on unmount (defensive).
  useEffect(() => {
    return () => {
      Sentry.setUser(null)
      Sentry.setTag('org_id', null)
      clearAnalyticsDefaults()
      analytics.reset()
    }
  }, [])
}

export default useTelemetryTags
